import React, { useEffect, useState } from 'react';
import { rgbToHex } from '../../utils/htmlSanitizer';

export default function HtmlInspectorPanel({ selectedElement, onUpdateProperty, onClose, onInsertImage, onDeleteElement }) {
    const [properties, setProperties] = useState({
        tagName: '',
        content: '',
        color: '#000000',
        backgroundColor: '#ffffff',
        backgroundColor: '#ffffff',
        padding: 0,
        margin: 0,
        borderRadius: 0,
        width: 'auto',
        height: 'auto'
    });

    useEffect(() => {
        if (!selectedElement) {
            return;
        }

        const styles = window.getComputedStyle(selectedElement);

        // Get content
        let content = '';
        if (selectedElement.tagName === 'INPUT' || selectedElement.tagName === 'TEXTAREA') {
            content = selectedElement.value || selectedElement.placeholder;
        } else if (selectedElement.tagName === 'IMG') {
            content = selectedElement.src;
        } else {
            content = selectedElement.innerText?.split('\n')[0] || '';
        }

        setProperties({
            tagName: selectedElement.tagName,
            content,
            color: rgbToHex(styles.color),
            backgroundColor: rgbToHex(styles.backgroundColor),
            padding: parseInt(styles.padding) || 0,
            margin: parseInt(styles.margin) || 0,
            borderRadius: parseInt(styles.borderRadius) || 0,
            width: styles.width,
            height: styles.height
        });
    }, [selectedElement]);

    const handleContentChange = (value) => {
        setProperties(prev => ({ ...prev, content: value }));
        onUpdateProperty('content', value);
    };

    const handleColorChange = (value) => {
        setProperties(prev => ({ ...prev, color: value }));
        onUpdateProperty('color', value);
    };

    const handleBackgroundChange = (value) => {
        setProperties(prev => ({ ...prev, backgroundColor: value }));
        onUpdateProperty('backgroundColor', value);
    };

    const handlePaddingChange = (value) => {
        setProperties(prev => ({ ...prev, padding: value }));
        onUpdateProperty('padding', `${value}px`);
    };

    const handleMarginChange = (value) => {
        setProperties(prev => ({ ...prev, margin: value }));
        onUpdateProperty('margin', `${value}px`);
    };

    const handleBorderRadiusChange = (value) => {
        setProperties(prev => ({ ...prev, borderRadius: value }));
        onUpdateProperty('borderRadius', `${value}px`);
    };

    const handleWidthChange = (value) => {
        setProperties(prev => ({ ...prev, width: value }));
        onUpdateProperty('width', value);
    };

    const handleHeightChange = (value) => {
        setProperties(prev => ({ ...prev, height: value }));
        onUpdateProperty('height', value);
    };

    if (!selectedElement) {
        return (
            <div className="flex-1 p-8 flex flex-col items-center justify-center text-gray-400">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                </div>
                <p className="text-sm text-center font-medium">Select an element to<br />customize styling</p>
            </div>
        );
    }

    return (
        <div className="flex-1 p-5 overflow-y-auto">
            <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-200">
                <h2 className="text-xs font-bold text-gray-600 uppercase">Visual Editor</h2>
                <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-[10px] font-mono border border-gray-200">
                    {properties.tagName}
                </span>
            </div>

            {/* Content */}
            <div className="mb-5">
                <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Text Content</label>
                <input
                    type="text"
                    value={properties.content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Text Color</label>
                    <div className="flex items-center gap-2 bg-white p-1 rounded border border-gray-300">
                        <input
                            type="color"
                            value={properties.color}
                            onChange={(e) => handleColorChange(e.target.value)}
                            className="h-6 w-6 rounded cursor-pointer bg-transparent border-none"
                        />
                        <input
                            type="text"
                            value={properties.color}
                            onChange={(e) => handleColorChange(e.target.value)}
                            className="w-full bg-transparent text-xs outline-none"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Background</label>
                    <div className="flex items-center gap-2 bg-white p-1 rounded border border-gray-300">
                        <input
                            type="color"
                            value={properties.backgroundColor}
                            onChange={(e) => handleBackgroundChange(e.target.value)}
                            className="h-6 w-6 rounded cursor-pointer bg-transparent border-none"
                        />
                        <input
                            type="text"
                            value={properties.backgroundColor}
                            onChange={(e) => handleBackgroundChange(e.target.value)}
                            className="w-full bg-transparent text-xs outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* Sliders */}
            <div className="space-y-4 mb-6">
                <div>
                    <div className="flex justify-between text-[10px] uppercase font-bold text-gray-600 mb-1">
                        <span>Padding</span>
                        <span className="text-gray-800">{properties.padding}px</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={properties.padding}
                        onChange={(e) => handlePaddingChange(e.target.value)}
                        className="w-full h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                </div>

                <div>
                    <div className="flex justify-between text-[10px] uppercase font-bold text-gray-600 mb-1">
                        <span>Margin</span>
                        <span className="text-gray-800">{properties.margin}px</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={properties.margin}
                        onChange={(e) => handleMarginChange(e.target.value)}
                        className="w-full h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                </div>

                <div>
                    <div className="flex justify-between text-[10px] uppercase font-bold text-gray-600 mb-1">
                        <span>Roundness</span>
                        <span className="text-gray-800">{properties.borderRadius}px</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={properties.borderRadius}
                        onChange={(e) => handleBorderRadiusChange(e.target.value)}
                        className="w-full h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                </div>
            </div>

            {/* Dimensions (Width/Height) */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Width</label>
                    <input
                        type="text"
                        value={properties.width}
                        onChange={(e) => handleWidthChange(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="e.g. 100px, 50%, auto"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Height</label>
                    <input
                        type="text"
                        value={properties.height}
                        onChange={(e) => handleHeightChange(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="e.g. 100px, 50%, auto"
                    />
                </div>
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-2 gap-2 mt-auto">
                <button
                    onClick={onInsertImage}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs py-2 px-3 rounded flex items-center justify-center gap-2 transition border border-gray-300"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Add Image
                </button>
                <button
                    onClick={onDeleteElement}
                    className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs py-2 px-3 rounded flex items-center justify-center gap-2 transition"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                </button>
            </div>
        </div>
    );
}
